import { z } from "zod";
import { BaseLayer } from "./map";
import { containerSchema } from "./container";

export const orgEntitlementSchema = z.enum([
  "overview", // standard map access
  "professional", // standard + data overlay
  "platform", // standard + data overlay + projects API
  "enterprise", // equivalent to platform (may expand in future)
  "turbine_api", // turbine API add-on
  "network_api", // network/infra API add-on
  "operational_addon", // operational data add-on (generation/revenue/network headroom charts)
  "trial", // trial access
]);

export type OrgEntitlement = z.infer<typeof orgEntitlementSchema>;

export interface OrganisationApiLicense {
  // the number of monthly requests allowed. 0 would be unlimited
  monthlyLimit: number;
  // unixtime in milliseconds that this license was granted to the company/user
  synchronizedAt: number;
}

export const USER_ROLES = ["admin", "billing_admin", "user"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export interface UserRecord {
  userId: string;
  // 1:1 with hubspot company id
  organisationId?: string;
  // unixtime in milliseconds that this license was granted to the company/user
  synchronizedAt: number;
  roles: UserRole[];
  // whether the user was explicitly provisioned by an admin
  provisioned?: boolean;
}

export interface OrganisationRecord {
  // 1:1 with hubspot company id
  organisationId: string;
  name: string;
  // RenewMap organisation entity which users will be able to link to
  renewmapOrgId?: string;
  // unixtime in milliseconds that this license was granted to the company/user
  synchronizedAt: number;
  // Array of entitlements (similar to user licenses)
  entitlements: OrgEntitlement[];
  // Total licensed seats for the organisation
  seats: number;
  // Unix timestamp in milliseconds or null for licence expiry date
  licenceExpiryDate: number | null;
  // Company email domains (lowercase, normalized)
  domains?: string[];
  // Required sign-in provider for this org (e.g. "saml.okta-acme", "google.com").
  // When set, blocking functions reject sign-ins that use any other provider.
  providerId?: string;
}

export interface UserApiKey {
  userId: string;
  key: string;
  created: number;
  expiry: number;
}

export interface UserApiKeyWithRecord extends UserOrgRecord {
  key: UserApiKey;
}

export interface UserOrgRecord {
  user: UserRecord;
  organisation: OrganisationRecord | null;
}

export interface UserPermissions {
  admin: boolean;
  billingAdmin: boolean;
  entitlements?: OrgEntitlement[];
  verified?: boolean;
  surveyed?: boolean;
  baseLayers: BaseLayer[];
  orgPreferences?: OrganisationPreferences;
}

export interface TokenResponse {
  token: string;
}

export interface ActivateUserInfo {
  email: string;
  userid: string;
  industry?: string;
  token?: string;
}

export interface ForceVerifyUserRequest {
  // the user in question
  userid: string;
  // what will be set against the user
  emailVerified: boolean;
}

export interface ForceResetPasswordUserRequest {
  // the user in question
  userid: string;
  setTemporaryPassword: boolean;
}

export interface ActivateUserResponse {
  // was a new contact created in hubspot
  created: boolean;
  // was a pending record made available for the logged-in user
  activated: boolean;
}

// Admin dashboard
export interface UserOrganisation {
  name?: string;
  id: string;
  domains?: string[];
  entitlements?: OrgEntitlement[];
  seats?: number;
  licenceExpiryDate?: number | null;
  renewmapOrgId?: string;
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  lastActive?: number;
  status: "Activated" | "Pending";
}

export interface OrganisationPreferences {
  "ai-capabilities": boolean;
}

export const DEFAULT_ORG_PREFERENCES: OrganisationPreferences = {
  "ai-capabilities": true,
};

export const organisationPreferencesSchema = z.object({
  "ai-capabilities": z.boolean(),
});

const baseLayerSchema = z
  .object({})
  .passthrough() as unknown as z.ZodType<BaseLayer>;

export const userPermissionsV2Schema = z.object({
  admin: z.boolean(),
  billingAdmin: z.boolean(),
  entitlements: z.array(orgEntitlementSchema).optional(),
  verified: z.boolean().optional(),
  surveyed: z.boolean().optional(),
  baseLayers: z.array(baseLayerSchema),
  orgPreferences: organisationPreferencesSchema.optional(),
  containers: z.array(containerSchema),
});

export type UserPermissionsV2 = z.infer<typeof userPermissionsV2Schema>;

export interface AdminBootstrap {
  org: UserOrganisation;
  users: User[];
  isSuperAdmin: boolean;
  preferences: OrganisationPreferences;
}
